import { useEffect, useState } from "react";

// Tiny shared state for "which project is active" — backed by localStorage so
// Sidebar's choice persists across reloads and other pages (HistoryPage, the
// chat-stream POST when creating a new thread) read the same value.
//
// Encoding:
//   absent / null   = no filter (show All)
//   "inbox"         = filter to items with no project assignment
//   "<number>"      = filter to that project id

const KEY = "abby_current_project";
const EVENT = "abby:current-project-change";

export type CurrentProject =
  | { kind: "all" }
  | { kind: "inbox" }
  | { kind: "project"; id: number };

function read(): CurrentProject {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return { kind: "all" };
    if (v === "inbox") return { kind: "inbox" };
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? { kind: "all" } : { kind: "project", id: n };
  } catch {
    return { kind: "all" };
  }
}

export function getCurrentProject(): CurrentProject {
  return read();
}

export function setCurrentProject(p: CurrentProject): void {
  try {
    if (p.kind === "all") localStorage.removeItem(KEY);
    else if (p.kind === "inbox") localStorage.setItem(KEY, "inbox");
    else localStorage.setItem(KEY, String(p.id));
  } catch {
    /* ignore quota / private mode */
  }
  // Notify all listeners in this tab.
  window.dispatchEvent(new CustomEvent(EVENT));
}

export function useCurrentProject(): [CurrentProject, (p: CurrentProject) => void] {
  const [value, setValue] = useState<CurrentProject>(read);
  useEffect(() => {
    const handler = () => setValue(read());
    window.addEventListener(EVENT, handler);
    // Cross-tab updates via the storage event.
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);
  return [value, setCurrentProject];
}
